import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/logo";
import { conflictPageCopy, type SsoConflictCode } from "@/lib/email-sync-policy";

const GESTAO_URL = "https://cadastro.rechovot.org.br";
const SUPPORT_EMAIL = "contato@rechovot.org.br";

const VALID_CODES: SsoConflictCode[] = [
  "shared_email",
  "identity_mismatch",
  "session_binding_failed",
];

export const Route = createFileRoute("/acesso/conflito-email")({
  validateSearch: (search: Record<string, unknown>) => ({
    motivo: typeof search.motivo === "string" ? search.motivo : "shared_email",
    ref: typeof search.ref === "string" ? search.ref.slice(0, 32) : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Conflito de e-mail · Beit Tefilah Rechovot" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: EmailConflictPage,
});

function EmailConflictPage() {
  const { motivo, ref } = useSearch({ from: "/acesso/conflito-email" });
  const code = (
    VALID_CODES.includes(motivo as SsoConflictCode) ? motivo : "shared_email"
  ) as SsoConflictCode;
  const copy = conflictPageCopy(code);

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-md text-center">
        <div className="mb-8 flex justify-center">
          <Logo size={96} />
        </div>
        <div className="rounded-2xl border border-border/60 bg-card p-8 shadow-lg">
          <ShieldAlert className="mx-auto mb-3 h-8 w-8 text-amber-600" aria-hidden="true" />
          <h1 className="mb-2 font-serif text-xl font-semibold text-primary">{copy.title}</h1>
          <p className="mb-4 text-sm leading-relaxed text-muted-foreground">{copy.body}</p>
          {ref && (
            <p className="mb-6 text-xs text-muted-foreground">
              Código para a secretaria: <span className="font-mono font-medium">{ref}</span>
            </p>
          )}
          <div className="flex flex-col gap-2">
            <Button asChild className="w-full">
              <a href={GESTAO_URL} target="_blank" rel="noopener noreferrer">
                Abrir Gestão de cadastros
              </a>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/auth">Voltar ao login</Link>
            </Button>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
                `Conflito de e-mail${ref ? ` (${ref})` : ""}`,
              )}`}
              className="mt-2 text-xs text-muted-foreground underline underline-offset-4"
            >
              Falar com a secretaria
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
